import AuthProvider from "@components/AuthProvider";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useEffect } from "react";
import CustomErrorBoundary from "@components/CustomErrorBoundary";
import { QueryClientProvider } from "@tanstack/react-query";
import { CreateQueryClient } from "@utils/ReactQuery/CreateQueryClient";
import { SignalRProvider } from "@context/SignalRProvider";
import AppInsightsProvider from "@context/AppInsightsProvider";
import InactivityLogout from "@components/InactivityLogout";
import { LicenseInfo } from "@mui/x-license";
import RouteAwareSuspense from "@components/RouteAwareResponse";
import { SingleTabRoute } from "@components/SingleTabOpen/SingleTabRoute";
import { isAuthenticatedAtom } from "@store/HostAtoms";
import { useAtomValue } from "jotai";
import PostAuthInitializer from "@components/PostAuthInitializer";

const TabView = React.lazy(() => import("data_parser/TabView"));
const HomePage = React.lazy(() => import("data_parser/HomePage"));
const SignedOutPage = React.lazy(() => import("data_parser/SignedOutPage"));
const HealthCheckPage = React.lazy(() => import("data_parser/HealthCheckPage"));

const queryClient = CreateQueryClient();

//License key for data grid pro
const dataGridLicenseKey = import.meta.env.VITE_DATA_GRID_LICENSE_KEY;
LicenseInfo.setLicenseKey(dataGridLicenseKey as string);

function App() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  useEffect(() => {
    const existingUserId = sessionStorage.getItem("userId");
    if (!existingUserId) {
      const newUserId = crypto.randomUUID();
      sessionStorage.setItem("userId", newUserId);
    }
  }, []);

  return (
    <AppInsightsProvider>
      <BrowserRouter>
        <Routes>
          {/* Public route */}
          <Route path="/signed-out" element={<SignedOutPage />} />
          <Route path="/health" element={<HealthCheckPage />} />

          {/* Protected routes under common AuthProvider + ErrorBoundary */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <InactivityLogout />
                  <CustomErrorBoundary componentName="FusionDataParser">
                    <QueryClientProvider client={queryClient}>
                      {isAuthenticated && (
                        <>
                          <PostAuthInitializer />
                          <SignalRProvider>
                            <Routes>
                              <Route path="/" element={
                                <RouteAwareSuspense>
                                  <SingleTabRoute>
                                    <HomePage />
                                  </SingleTabRoute>
                                </RouteAwareSuspense>
                              } />
                              <Route path="*" element={<Navigate to="/" />} />
                              <Route
                                path="/fusion-analyzer/:liUploadID/upload"
                                element={
                                  <RouteAwareSuspense>
                                    <SingleTabRoute>
                                      <TabView />
                                    </SingleTabRoute>
                                  </RouteAwareSuspense>
                                } />
                              <Route
                                path="/fusion-analyzer/:liUploadID/snow"
                                element={<RouteAwareSuspense><TabView /></RouteAwareSuspense>}
                              />
                            </Routes>
                          </SignalRProvider>
                        </>
                      )}
                    </QueryClientProvider>
                  </CustomErrorBoundary>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </AppInsightsProvider>
  );
}

export default App;
